import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dah7g1jp14a1h3mr6e1g";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，我们会在每个工作日早上把盘点发到你的邮箱。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，我们会在每个工作日早上把盘点发到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交没有成功，请稍后再试一次。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络好像不太顺畅，提交没有成功，请稍后再试。");
    }
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <form
        data-readdy-form
        id="daily-digest-subscribe"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 sm:flex-row sm:items-center"
        noValidate
      >
        <label className="sr-only" htmlFor="subscribe-email">
          邮箱地址
        </label>
        <span className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
          <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
            <i className="ri-mail-line text-base leading-none" aria-hidden="true" />
          </span>
          <input
            id="subscribe-email"
            type="email"
            name="email"
            required
            placeholder="输入邮箱，订阅每日盘点"
            className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </span>
        <label className="sr-only" htmlFor="subscribe-interest">
          最关注的分类
        </label>
        <span className="flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 sm:w-44">
          <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
            <i className="ri-price-tag-3-line text-base leading-none" aria-hidden="true" />
          </span>
          <select
            id="subscribe-interest"
            name="interest"
            defaultValue="娱乐八卦"
            className="h-full w-full cursor-pointer bg-transparent text-sm text-foreground-900 outline-none"
          >
            <option value="娱乐八卦">娱乐八卦</option>
            <option value="影视综">影视综</option>
            <option value="社会焦点">社会焦点</option>
            <option value="科技数码">科技数码</option>
            <option value="体育赛事">体育赛事</option>
            <option value="生活方式">生活方式</option>
          </select>
        </span>

        <span className="field-hidden" aria-hidden="true">
          <label htmlFor="subscribe-website-alt">请勿填写此项</label>
          <input
            id="subscribe-website-alt"
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </span>

        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex h-11 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? "提交中…" : "订阅盘点"}
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-send-plane-line text-base leading-none" aria-hidden="true" />
          </span>
        </button>
      </form>

      {message && (
        <p
          aria-live="polite"
          className={`text-sm ${status === "error" ? "text-primary-700" : "text-secondary-700"}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}